import {
	Alert,
	Scrollbar,
	Button,
	FlexColumn,
	FlexRow,
	Heading,
	IconTooltip,
	Modal,
	Separator,
	NumberField,
	Table,
	Text,
	Tooltip,
	ComboBox,
	Label,
} from '@forecast-it/design-system';
import moment from 'moment-timezone';
import React, {useEffect, useMemo, useState} from 'react';
import {showCurrencyHistoryModal} from './CurrencyHistoryModal';
import {AddCurrencyRateRow} from './AddCurrencyRateRow';
import {getRateStatus} from './ExchangeRateUtil';
import {FormattedMessage, useIntl} from 'react-intl';
import styled from 'styled-components';
import {SOCKET_ACTION} from '../../../constants';
import {getCurrencyByCode, getAvailableCurrenciesToAdd} from '../../../data/currencyHelpers';
import {hasFeatureFlag} from '../../shared/util/FeatureUtil';
import CreateExchangeRateMutation from '../../../mutations/CreateExchangeRateMutation';
import DeleteExchangeRateMutation from '../../../mutations/delete_exchange_rate_mutation';
import {MODAL_TYPE, showModal, closeModal} from '../../shared/components/modals/generic_modal_conductor';
import Util from '../../shared/util/util';
import {ConfirmationModalPrimaryButton} from '../../shared/components/modals/general/ConfirmationModal';

import {ExchangeRate} from './types';

const AlertContainer = styled.div`
	margin: 16px 0px;
`;

const HeadingContainer = styled.div`
	margin: 12px 0px;
`;

const InputContainer = styled.div`
	// Fixed width to align with the table cell width
	width: 344px;

	// Override the placeholder font size and colour from reset.scss, to use design system font size
	.currency-select-combobox::placeholder,
	.currency-select-combobox::-webkit-input-placeholder {
		font-size: inherit;
		color: inherit;
	}
`;

interface Company {
	id: string;
	currency: string;
	name: string;
	timezone: string;
}

interface ExchangeRateRow extends ExchangeRate {
	title: string;
	subtitle: string;
	ratePrimary: string;
	rateSecondary: string;
	createdAtFormatted: string;
}

export const Currencies = ({company, exchangeRates}: {company: Company; exchangeRates: ExchangeRate[]}) => {
	const {formatMessage} = useIntl();
	const [isDismissed, setIsDismissed] = useState(false);
	const [showAddRow, setShowAddRow] = useState(false);
	const [selectedCurrencyCode, setSelectedCurrencyCode] = useState<string | null>(null);
	const [rate, setRate] = useState<number | undefined>(undefined);
	const [showCurrencySuccessAlert, setShowCurrencySuccessAlert] = useState(false);
	const [showCurrencyDeleteAlert, setShowCurrencyDeleteAlert] = useState(false);
	const [showCurrencyRateAddedAlert, setShowCurrencyRateAddedAlert] = useState(false);

	const [companyNow, setCompanyNow] = useState(() => moment().tz(company.timezone));
	useEffect(() => {
		const id = setInterval(() => setCompanyNow(moment().tz(company.timezone)), 60_000);
		return () => clearInterval(id);
	}, [company.timezone]);
	const companyTimeLabel = `${companyNow.format('DD MMM YYYY, LT')} ${Util.getTimeZoneLabel(
		companyNow.toDate(),
		company.timezone ?? 'UTC'
	)}`;

	const isInvalidRate = rate !== undefined && rate <= 0;

	useEffect(() => {
		if (!showCurrencySuccessAlert) return;
		const timer = setTimeout(() => setShowCurrencySuccessAlert(false), 5000);
		return () => clearTimeout(timer);
	}, [showCurrencySuccessAlert]);

	useEffect(() => {
		if (!showCurrencyDeleteAlert) return;
		const timer = setTimeout(() => setShowCurrencyDeleteAlert(false), 5000);
		return () => clearTimeout(timer);
	}, [showCurrencyDeleteAlert]);

	useEffect(() => {
		if (!showCurrencyRateAddedAlert) return;
		const timer = setTimeout(() => setShowCurrencyRateAddedAlert(false), 5000);
		return () => clearTimeout(timer);
	}, [showCurrencyRateAddedAlert]);

	const activeExchangeRates = useMemo(() => {
		const today = moment();
		return exchangeRates.filter(exchangeRate => getRateStatus(exchangeRate, today) === 'current');
	}, [exchangeRates]);

	const data = useMemo(
		() =>
			activeExchangeRates.map(exchangeRate => {
				const currency = getCurrencyByCode(exchangeRate.currency);

				const rate = exchangeRate.rate ?? 0;

				return {
					title: currency ? `${currency.code} (${currency.symbol})` : exchangeRate.currency,
					subtitle: currency ? currency.name : '',
					ratePrimary: `${rate} ${exchangeRate.currency}`,
					rateSecondary: `1 ${company.currency} = ${rate} ${exchangeRate.currency}`,
					...exchangeRate,
					createdAtFormatted: Util.formatUtcDateTimeToLocalTimezone(exchangeRate.createdAt),
				};
			}),
		[activeExchangeRates, company]
	);

	const expandedCatalog = hasFeatureFlag('sage_intacct_requested_currencies_for_forecast');
	const availableCurrencies = useMemo(
		() =>
			getAvailableCurrenciesToAdd(
				company.currency,
				exchangeRates.map(er => er.currency),
				expandedCatalog
			),
		[company.currency, exchangeRates, expandedCatalog]
	);

	const clearAlerts = () => {
		setShowCurrencySuccessAlert(false);
		setShowCurrencyDeleteAlert(false);
		setShowCurrencyRateAddedAlert(false);
	};

	const resetAddCurrencyRow = () => {
		setShowAddRow(false);
		setSelectedCurrencyCode(null);
		setRate(undefined);
	};

	const handleAddCurrency = () => {
		clearAlerts();

		if (!selectedCurrencyCode || rate === undefined || rate <= 0) {
			return;
		}

		setShowAddRow(false);

		Util.CommitMutation(
			CreateExchangeRateMutation,
			{
				companyId: company.id,
				currency: selectedCurrencyCode,
				rate: rate,
			},
			() => {
				resetAddCurrencyRow();
				setShowCurrencySuccessAlert(true);
			},
			true
		);
	};

	const handleDeleteCurrency = (row: ExchangeRateRow, onAfterDelete?: () => void) => {
		clearAlerts();
		const currencyEntry = getCurrencyByCode(row.currency);

		const callbackPositive = () => {
			Util.CommitMutation(
				DeleteExchangeRateMutation,
				{
					companyId: company.id,
					id: row.id,
					currency: row.currency,
				},
				() => {
					setShowCurrencyDeleteAlert(true);
					onAfterDelete?.();
				},
				true
			);
		};

		showModal({
			type: MODAL_TYPE.CONFIRMATION,
			title: formatMessage({id: 'settings_finance.delete_exchange_rate_modal_title'}),
			description: (
				<div style={{whiteSpace: 'pre-line'}}>
					<FormattedMessage
						id="settings_finance.delete_exchange_rate_modal_description"
						values={{
							currencyLabel: `${currencyEntry?.name} ${currencyEntry?.code}`,
							confirmText: SOCKET_ACTION.DELETE,
							b: (chunks: React.ReactNode) => <b>{chunks}</b>,
						}}
					/>
				</div>
			),
			modalSize: Modal.SIZE.MEDIUM,
			confirmPhrase: SOCKET_ACTION.DELETE,
			includeDefaultMessage: false,
			confirmationModalPrimaryButton: ConfirmationModalPrimaryButton.DELETE,
			callbackPositive,
		});
	};

	return (
		<FlexColumn>
			{!isDismissed && (
				<AlertContainer>
					<Alert
						text={formatMessage({id: 'settings_finance.currencies.info_banner'})}
						type={Alert.TYPE.INFORMATION}
						onDismiss={() => setIsDismissed(true)}
					/>
				</AlertContainer>
			)}
			{showCurrencySuccessAlert && (
				<AlertContainer>
					<Alert
						text={formatMessage({id: 'settings_finance.currencies.alert.currency_added_alert'})}
						type={Alert.TYPE.SUCCESS}
						onDismiss={() => setShowCurrencySuccessAlert(false)}
					/>
				</AlertContainer>
			)}
			{showCurrencyDeleteAlert && (
				<AlertContainer>
					<Alert
						text={formatMessage({id: 'settings_finance.currencies.alert.currency_deleted_alert'})}
						type={Alert.TYPE.SUCCESS}
						onDismiss={() => setShowCurrencyDeleteAlert(false)}
					/>
				</AlertContainer>
			)}
			{showCurrencyRateAddedAlert && (
				<AlertContainer>
					<Alert
						text={formatMessage({id: 'settings_finance.currencies.alert.currency_rate_added_alert'})}
						type={Alert.TYPE.SUCCESS}
						onDismiss={() => setShowCurrencyRateAddedAlert(false)}
					/>
				</AlertContainer>
			)}

			<HeadingContainer>
				<FlexColumn gap="l">
					<FlexRow justifyContent="space-between" alignItems="center">
						<Heading size={4}>{formatMessage({id: 'settings_finance.currencies.page_title'})}</Heading>
						<Text color="black" noWrap>
							{formatMessage(
								{id: 'settings_finance.currencies.company_date_time_label'},
								{
									companyName: company.name,
									companyTimeLabel,
									b: (chunks: React.ReactNode) => <b>{chunks}</b>,
								}
							)}
						</Text>
					</FlexRow>
					<Separator />
				</FlexColumn>
			</HeadingContainer>

			<Scrollbar maxHeight={560} hasFocusableContent>
				<Table width={'100%'} data={data} stickyHeader>
					<Table.TitleSubtitleColumn
						width="360px"
						accessorKey="currencyCodeSymbol"
						header={formatMessage({id: 'common.currency'})}
						allowSorting
						renderTitle={(row: ExchangeRateRow) => row.title}
						renderSubtitle={(row: ExchangeRateRow) => row.subtitle}
						renderTitleAccessory={(row: ExchangeRateRow) =>
							row.isUsed ? (
								<IconTooltip icon="lockClosed" iconColor="onlight">
									<IconTooltip.Item
										details={formatMessage({id: 'settings_finance.currencies.locked_tooltip.description'})}
									/>
								</IconTooltip>
							) : null
						}
					/>
					<Table.TitleSubtitleColumn
						width="360px"
						accessorKey="ratePrimary"
						header={formatMessage({id: 'settings_finance.currencies.table.currency_rate'})}
						allowSorting
						renderTitle={(row: ExchangeRateRow) => row.ratePrimary}
						renderSubtitle={(row: ExchangeRateRow) => row.rateSecondary}
					/>
					<Table.TextColumn
						accessorKey="createdAtFormatted"
						header={formatMessage({id: 'global_filter_field.created_at'})}
						allowSorting
						sortingFn="text"
					/>
					<Table.ActionColumn header={formatMessage({id: 'common.actions'})} hideHeader>
						<Table.ActionColumn.Option
							onClick={(row: ExchangeRateRow) =>
								showCurrencyHistoryModal({
									currencyCode: row.currency,
									exchangeRates: exchangeRates.filter(r => r.currency === row.currency),
									company,
									onRateAdded: () => setShowCurrencyRateAddedAlert(true),
									onDelete: () => handleDeleteCurrency(row, closeModal),
								})
							}
						>
							<FormattedMessage id="settings_finance.currencies.table.view_add_rate" />
						</Table.ActionColumn.Option>
						<Table.ActionColumn.Option
							hidden={(row: ExchangeRateRow) => !!row.isUsed}
							onClick={(row: ExchangeRateRow) => handleDeleteCurrency(row)}
						>
							<FormattedMessage id="common.delete" />
						</Table.ActionColumn.Option>
					</Table.ActionColumn>
				</Table>
			</Scrollbar>

			{showAddRow ? (
				<AddCurrencyRateRow
					onConfirm={handleAddCurrency}
					onCancel={resetAddCurrencyRow}
					isConfirmDisabled={!selectedCurrencyCode || rate === undefined || isInvalidRate}
				>
					<InputContainer>
						<ComboBox
							defaultItems={availableCurrencies}
							placeholder={formatMessage({id: 'settings_finance.currencies.select_currency'})}
							onChange={setSelectedCurrencyCode}
							width={'flex'}
							customClassName="currency-select-combobox"
						>
							{item => (
								<ComboBox.Item key={item.code} textValue={item.name}>
									<Label>
										{item.code} ({item.symbol})
									</Label>
									<Text> - {item.name}</Text>
								</ComboBox.Item>
							)}
						</ComboBox>
					</InputContainer>

					<InputContainer>
						{(() => {
							const field = (
								<NumberField
									value={rate}
									onChange={value => setRate(value)}
									prefixText={`1 ${company.currency} =`}
									suffixText={selectedCurrencyCode ?? ''}
									align={NumberField.ALIGN.RIGHT}
									maximumFractionDigits={2}
									boldValue={true}
									required
									hasError={isInvalidRate}
								/>
							);
							return isInvalidRate ? (
								<Tooltip
									interactable={false}
									content={formatMessage({
										id: 'settings_finance.currencies.history_modal.error_negative_rate',
									})}
									width={Tooltip.WIDTH.l}
								>
									{field}
								</Tooltip>
							) : (
								field
							);
						})()}
					</InputContainer>
				</AddCurrencyRateRow>
			) : (
				<Button onPress={() => setShowAddRow(true)} icon="plus" emphasis="low">
					{formatMessage({id: 'settings_finance.currencies.add_currency_text'})}
				</Button>
			)}
		</FlexColumn>
	);
};
